import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { formatCurrency } from 'utils';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useModal } from 'hooks/useModal';
import { useParams } from 'react-router-dom';
import { DateFormats } from 'constants/common';
import { customersApi } from 'modules/customers/api';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { useDealActivityUpdated, useSmsActivityReceived } from 'hooks/useSubscriptions';
import { useRefetchOnClose } from 'modules/customers/hooks/useRefetchOnClose';
import { useActivityHubTabDataFetch } from 'modules/customers/hooks/useActivityHubTabDataFetch';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import {
  ActivityListFilters,
  ActivityPageTabs,
  DealActivityFilters,
  DealActivityList,
  dealActivityApi,
  EventCard,
  initialFilters,
  MessagingPromo,
  SmsConversationCard,
  SmsConversationData,
  useEventIdParam,
} from 'modules/dealActivity';
import { APIRequestSort, DictionaryItem, ModalsKeys, Nullable, SelectOption, TabsOption } from 'global';
import { TabContainer } from 'pages/DealActivity/TabContainer';
import { Email, Event, LocalPhone, Sms, TextSnippet } from '@mui/icons-material';
import { CustomerVendorOpportunity, DealActivityLinkType } from '@trnsact/trnsact-shared-types';

// Subscriptions are scoped to a single VO, so the hub mounts one of these per customer VO
const VoActivitySubscriptions = ({ voId, onEvent }: { voId: string; onEvent: VoidFunction }) => {
  // useEffect is used instead of onData to avoid stale-closure issues in React 17.
  const { data: latestSmsEvent } = useSmsActivityReceived(voId);
  useEffect(() => {
    if (latestSmsEvent?.smsActivityReceived) onEvent();
  }, [latestSmsEvent]);

  const { data: latestActivityEvent } = useDealActivityUpdated(voId);
  useEffect(() => {
    if (latestActivityEvent?.dealActivityUpdated) onEvent();
  }, [latestActivityEvent]);

  return null;
};

export const CustomerDetailsActivityHubTab = () => {
  const { id: customerAccountId } = useParams<{ id: string }>();

  const parentType = DealActivityLinkType.Vo;

  const { handleOpen: onOpenDialog } = useModal(ModalsKeys.ActivityDialog);
  const { handleOpen: onOpenCallDialog } = useModal(ModalsKeys.CallActivityDialog);
  const { handleOpen: onOpenNoteDialog } = useModal(ModalsKeys.NoteActivityDialog);
  const { handleOpen: onOpenNewChatDialog } = useModal(ModalsKeys.NewMessageDialog);
  const { handleOpen: onOpenCreateEventDialog, isOpen: isCreateEventDialogOpen } = useModal(
    ModalsKeys.CreateEventDialog
  );

  const { eventId } = useEventIdParam();

  const isMessagingEnabled = useSelector<any, boolean>(portalConfigurationsSelectors.allowMessagingModule);

  // New activities from the hub are linked to the customer account rather than a specific VO
  const newActivityParent = { parentId: customerAccountId, parentType: DealActivityLinkType.Account };

  const [currentTab, setCurrentTab] = useState<ActivityPageTabs>(ActivityPageTabs.ALL);

  const [sorting, setSorting] = useState<APIRequestSort<'created_date_time'>>({
    key: 'created_date_time',
    value: 'desc',
  });
  const [filters, setFilters] = useState<ActivityListFilters>(initialFilters);
  const [selectedConversation, setSelectedConversation] = useState<Nullable<SmsConversationData>>(null);

  const { activityList, prevActivityList, loading, refetch } = useActivityHubTabDataFetch({
    sorting,
    filters,
    customerAccountId,
    activityType: currentTab,
  });

  // Events are changed in the drawer or in the create dialog, so the list is refreshed when either closes
  useRefetchOnClose(Boolean(eventId), refetch);
  useRefetchOnClose(isCreateEventDialogOpen, refetch);

  // SMS is sent through a VO (Twilio conversations are VO-scoped), so new chats must target one of the customer's VOs
  const { data: opportunitiesData } = useQuery(customersApi.getCustomerOpportunities, {
    variables: { customerAccountId },
  });

  const { data: usersData } = useQuery<{ dealActivity: { dealContacts: DictionaryItem[] } }>(
    dealActivityApi.dealContacts
  );

  const ownerOptions = usersData?.dealActivity?.dealContacts ?? [];

  const voOptions = useMemo<SelectOption[]>(() => {
    const opportunities: CustomerVendorOpportunity[] = opportunitiesData?.customer?.customerOpportunities?.list ?? [];

    return opportunities.map(o => ({
      value: o.vendorOpportunityId,
      label: [
        moment(o.createdDateTime).format(DateFormats.DateTimeShort12Hour),
        formatCurrency(o.invoiceTotal),
        o.transactionStage,
      ]
        .filter(Boolean)
        .join(' • '),
    }));
  }, [opportunitiesData]);

  const tabs: TabsOption<ActivityPageTabs>[] = [
    {
      label: 'All Activities',
      value: ActivityPageTabs.ALL,
      component: (
        <TabContainer
          tabTitle="All Activity"
          actionName="ADD NEW"
          action={() => onOpenDialog(newActivityParent)}
          listSlot={
            <DealActivityList
              list={activityList}
              loading={loading}
              parentType={parentType}
              opportunityOptions={voOptions}
              onOpenConversation={setSelectedConversation}
            />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Email',
      iconStart: <Email />,
      value: ActivityPageTabs.EMAIL,
      component: (
        <TabContainer
          tabTitle="Email"
          listSlot={<DealActivityList list={activityList} loading={loading} parentType={parentType} />}
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Messages',
      iconStart: <Sms />,
      value: ActivityPageTabs.SMS,
      component: isMessagingEnabled ? (
        <TabContainer
          tabTitle="Messages"
          actionName="NEW MESSAGE"
          // Opportunities are sorted newest first, so the latest VO is the default
          action={
            voOptions.length
              ? () =>
                  onOpenNewChatDialog({
                    voId: voOptions[0].value,
                    voOptions,
                    activityList,
                    onConfirm: setSelectedConversation,
                  })
              : undefined
          }
          listSlot={
            <DealActivityList
              list={activityList}
              loading={loading}
              parentType={parentType}
              onOpenConversation={setSelectedConversation}
            />
          }
        />
      ) : (
        <MessagingPromo />
      ),
    },
    {
      label: 'Call',
      iconStart: <LocalPhone />,
      value: ActivityPageTabs.CALL,
      component: (
        <TabContainer
          tabTitle="Call"
          actionName="ADD NEW CALL"
          action={() => onOpenCallDialog(newActivityParent)}
          listSlot={<DealActivityList list={activityList} loading={loading} parentType={parentType} />}
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Notes',
      iconStart: <TextSnippet />,
      value: ActivityPageTabs.NOTE,
      component: (
        <TabContainer
          tabTitle="Notes"
          actionName="ADD NEW NOTE"
          action={() => onOpenNoteDialog(newActivityParent)}
          listSlot={
            <DealActivityList /* parentId={parentId} */ list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Events',
      iconStart: <Event />,
      value: ActivityPageTabs.EVENT,
      component: (
        <TabContainer
          tabTitle="Events"
          actionName="ADD NEW EVENT"
          action={() => onOpenCreateEventDialog({ accountId: customerAccountId })}
          listSlot={
            <DealActivityList
              list={activityList}
              loading={loading}
              parentType={parentType}
              opportunityOptions={voOptions}
            />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              ownerOptions={ownerOptions}
              opportunityOptions={voOptions}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
  ];

  return (
    <CardContainer isLoading={loading && !prevActivityList}>
      {voOptions.map(({ value }) => (
        <VoActivitySubscriptions key={value} voId={value} onEvent={refetch} />
      ))}

      {eventId && <EventCard id={eventId} />}

      {selectedConversation ? (
        <SmsConversationCard
          data={selectedConversation}
          parentType={parentType}
          onBack={() => {
            setSelectedConversation(null);
            refetch();
          }}
        />
      ) : (
        <TabsContainer<ActivityPageTabs> tabs={tabs} onTabChange={setCurrentTab} />
      )}
    </CardContainer>
  );
};
